import { useNavigate } from 'react-router-dom';

import { Card, Badge, Sparkline } from '../ui/primitives';
import { Icon } from '../ui/Icon';
import { useIsMobile } from '../../lib/useBreakpoint';

const ROLE_TO_BADGE_VARIANT = {
  owner:    'navy',
  admin:    'info',
  reviewer: 'warn',
  staff:    'info',
  client:   'neutral',
};

const PLAN_TO_BADGE = {
  free:     { variant: 'neutral', label: 'Free' },
  pro:      { variant: 'accent',  label: 'Pro' },
  business: { variant: 'navy',    label: 'Business' },
};

/**
 * WorkspaceCard — one team on the hub grid.
 *
 * Props.w shape (from Hub):
 *   {
 *     id, name, slug, color, plan,
 *     roleSlug, roleName,           // current user's role in this team
 *     isClient,                     // whether the user's role is client-facing
 *     members,                      // member count
 *     usage, limit,                 // classifications used/limit (null if unknown)
 *     trend,                        // array of numbers for sparkline (or null)
 *     pending,                      // # uploads in_review (for staff)
 *     missing,                      // # docs the client still owes (for client cards)
 *     industry,                     // optional industry label
 *   }
 */
export default function WorkspaceCard({ w }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const initials = (w.name || '?').split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();
  // Phase 2: plan badge removed — plan is user-level now
  const roleVariant = ROLE_TO_BADGE_VARIANT[(w.roleSlug || '').toLowerCase()] || 'neutral';

  const onCardClick = () => {
    if (w.isClient) {
      navigate(`/portal?team=${w.id}`);
    } else {
      navigate(`/workspace/${w.id}`);
    }
  };

  const cardPad = isMobile ? 14 : 18;

  return (
    <Card
      hover
      onClick={onCardClick}
      style={{
        padding: 0, overflow: 'hidden',
        minHeight: isMobile ? 0 : 260,
        display: 'flex', flexDirection: 'column',
        width: '100%', maxWidth: '100%',
      }}
    >
      {/* Header: logo + name + role/plan + more menu */}
      <div style={{
        padding: cardPad,
        display: 'flex', alignItems: 'flex-start',
        gap: isMobile ? 10 : 12,
        minWidth: 0,
      }}>
        <div style={{
          width: isMobile ? 36 : 44, height: isMobile ? 36 : 44,
          borderRadius: isMobile ? 10 : 12,
          background: w.color || '#0a0f2e', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: isMobile ? 13 : 15, letterSpacing: '-0.02em',
          flexShrink: 0,
        }}>
          {initials}
        </div>

        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, maxWidth: '100%' }}>
            <h3 style={{
              fontSize: isMobile ? 14 : 15, fontWeight: 700, letterSpacing: '-0.01em',
              color: 'var(--text)', margin: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              flex: 1, minWidth: 0,
            }}>
              {w.name}
            </h3>
          </div>
          <div style={{
            fontSize: isMobile ? 11 : 11.5, color: 'var(--text-3)', marginTop: 2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {[w.industry, w.members != null ? `${w.members} member${w.members === 1 ? '' : 's'}` : null]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>

        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          style={{
            border: 'none', background: 'transparent', cursor: 'pointer',
            color: 'var(--text-muted)', padding: 4, borderRadius: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
          title="More options"
        >
          <Icon.more size={16} />
        </button>
      </div>

      {/* Body: client message OR usage + sparkline */}
      <div style={{ padding: `0 ${cardPad}px ${isMobile ? 10 : 12}px`, minWidth: 0 }}>
        {w.isClient ? (
          w.missing > 0 ? (
            <div style={{
              fontSize: isMobile ? 12 : 12.5, color: 'var(--text-2)',
              padding: isMobile ? '7px 10px' : '8px 12px',
              background: 'var(--warn-bg)', borderRadius: 8,
              border: '1px solid rgba(245,158,11,0.22)',
            }}>
              <strong style={{ color: 'var(--warn)' }}>{w.missing} document{w.missing === 1 ? '' : 's'}</strong>
              {' '}still pending your upload
            </div>
          ) : (
            <div style={{
              fontSize: isMobile ? 12 : 12.5, color: 'var(--text-2)',
              padding: isMobile ? '7px 10px' : '8px 12px',
              background: 'var(--ok-bg)', borderRadius: 8,
              border: '1px solid rgba(61,199,91,0.22)',
            }}>
              <Icon.check size={12} stroke="var(--ok)" style={{ marginRight: 6, verticalAlign: 'middle' }} />
              All documents submitted
            </div>
          )
        ) : (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 10, minWidth: 0,
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 2, fontWeight: 500 }}>
                This month
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, letterSpacing: '-0.02em' }}>
                  {w.usage != null ? w.usage : '—'}
                </span>
                {/* Hide the "/ N docs" suffix on Business / unlimited (limit ≥ 99999 means effectively no cap) */}
                {w.limit != null && w.limit < 99999 ? (
                  <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                    / {w.limit} docs
                  </span>
                ) : (
                  <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                    docs reviewed
                  </span>
                )}
              </div>
            </div>
            {/* Hide sparkline on mobile to save space */}
            {!isMobile && w.trend && w.trend.length > 0 && (
              <Sparkline data={w.trend} color="#3dc75b" height={28} width={90} />
            )}
          </div>
        )}
      </div>

      {/* Footer: role badge + pending count + arrow */}
      <div style={{
        marginTop: 'auto',
        padding: isMobile ? `8px ${cardPad}px` : '12px 18px',
        background: 'var(--surface-2)',
        borderTop: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 8, minWidth: 0,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          flexWrap: 'wrap', flex: 1, minWidth: 0,
        }}>
          <Badge variant={roleVariant} size="xs">{w.roleName || w.roleSlug || 'Member'}</Badge>
          {w.pending > 0 && (
            <Badge variant="warn" size="xs" dot>
              {w.pending} to review
            </Badge>
          )}
          {w.rejected > 0 && (
            <Badge variant="danger" size="xs" dot>
              {w.rejected} rejected
            </Badge>
          )}
        </div>
        <Icon.arrowR size={14} stroke="var(--text-3)" style={{ flexShrink: 0 }} />
      </div>
    </Card>
  );
}
